import type { TFunction } from 'i18next';

import type { MetricInProgressBucket } from '@/components/charts/generic';
import type { FeedingResolution } from '@/domain/feedingDashboard';

import { formatDashboardDay } from './dashboardDay';

const DAY_MS = 24 * 60 * 60 * 1000;

/** Monday of the ISO week holding `day`, the key the backend gives a weekly bucket. */
export function isoWeekStart(day: string): string {
    const time = Date.parse(day);
    const weekday = new Date(time).getUTCDay();
    return new Date(time - ((weekday + 6) % 7) * DAY_MS).toISOString().slice(0, 10);
}

/**
 * The site's today when the period reaches it, otherwise null.
 *
 * Both timeline markers and the partial-week captions share this rule, so a week the
 * captions leave to the marker is always marked, and the other way round. The backend
 * clamps `to` to `siteToday`, so reaching it means ending on it.
 */
export function todayInPeriod(siteToday: string | null, periodEnd: string): string | null {
    return siteToday !== null && periodEnd >= siteToday ? siteToday : null;
}

export interface TodayMarker {
    /** The bucket to mark on the chart, with its tooltip. */
    inProgress: MetricInProgressBucket;
    /** Shown under a weekly chart, where the marked bar spans more than today. */
    caption: string | null;
}

export interface TodayMarkerInput {
    /** The site's day in progress; null from a backend that predates the field. */
    siteToday: string | null;
    /** The selected period end (`overview.to`). */
    periodEnd: string;
    resolution: FeedingResolution;
    /** The bucket keys the chart draws. */
    buckets: string[];
}

/**
 * The timeline bucket that holds the site's today, marked as in progress (IPS 3.2).
 *
 * Both timelines (Feed & Weight, pellet) draw today whenever the period includes it,
 * with only what has arrived so far: typically the plan, while the actual comes the next
 * day. Unmarked, that bucket reads as a complete day with a shortfall. Daily, the bucket
 * is today; weekly, the current week, which also gets a caption because its bar mixes
 * complete days with the incomplete one.
 *
 * Null when there is nothing to mark: no `siteToday` (a backend that predates it), a
 * period that ends before today, or no bucket for today on the chart (one that only draws
 * buckets with values). The period end is checked on its own because a weekly bucket
 * is keyed by its Monday: a period ending the day before today, in today's ISO week, has
 * that Monday on its axis, but the backend closed the bucket at `to`, so it is complete.
 */
export function todayMarker(input: TodayMarkerInput, t: TFunction): TodayMarker | null {
    const today = todayInPeriod(input.siteToday, input.periodEnd);
    if (today === null) return null;
    const key = input.resolution === 'week' ? isoWeekStart(today) : today;
    if (!input.buckets.includes(key)) return null;
    const day = formatDashboardDay(today);
    if (input.resolution === 'week') {
        const text = t('feedingModule.charts.weekInProgress', { today: day });
        return { inProgress: { key, tooltip: text }, caption: text };
    }
    return {
        inProgress: { key, tooltip: t('feedingModule.charts.todayInProgress', { today: day }) },
        caption: null,
    };
}
